import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { RecruitmentSourceCard } from './RecruitmentSourceCard'
import { CandidateScoutingModal } from './CandidateScoutingModal'
import { PoachingModal } from './PoachingModal'
import { RECRUITMENT_SOURCE_INFO } from '@/constants/recruitmentSources'
import { useActionPoints } from '@/hooks/useActionPoints'
import type { Candidate, IdeologyVector, RecruitmentSource } from '@/types'

const RECRUITMENT_SOURCE_KEYS = Object.keys(RECRUITMENT_SOURCE_INFO) as RecruitmentSource[]

interface RecruitmentPanelProps {
  partyId: string
  nationId: string
  partyIdeology: IdeologyVector
  currentTick: number
  onRecruited?: (candidate: Candidate) => void
  onAssignRole?: (candidate: Candidate) => void
  className?: string
}

export function RecruitmentPanel({
  partyId,
  nationId,
  partyIdeology,
  currentTick,
  onRecruited,
  onAssignRole,
  className,
}: RecruitmentPanelProps) {
  const [selectedSource, setSelectedSource] = useState<RecruitmentSource | null>(null)
  const { available: availableAP } = useActionPoints(partyId, currentTick)

  const handleScout = (source: RecruitmentSource) => {
    setSelectedSource(source)
  }

  const handleModalClose = (open: boolean) => {
    if (!open) {
      setSelectedSource(null)
    }
  }

  return (
    <>
      <Card className={className}>
        <CardHeader>
          <CardTitle>Recruit Candidates</CardTitle>
          <CardDescription>
            Scout talented individuals to join your party. Each source produces candidates with
            different strengths and backgrounds. Select a source, choose your preferred candidate,
            and they will join immediately.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {RECRUITMENT_SOURCE_KEYS.map((source) => (
              <RecruitmentSourceCard
                key={source}
                source={source}
                onScout={handleScout}
                availableAP={availableAP}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {selectedSource && selectedSource !== 'poached' && (
        <CandidateScoutingModal
          key={selectedSource}
          open={!!selectedSource}
          onOpenChange={handleModalClose}
          source={selectedSource}
          partyId={partyId}
          nationId={nationId}
          partyIdeology={partyIdeology}
          currentTick={currentTick}
          onRecruited={onRecruited}
          onAssignRole={onAssignRole}
        />
      )}

      {selectedSource === 'poached' && (
        <PoachingModal
          open={selectedSource === 'poached'}
          onOpenChange={handleModalClose}
          partyId={partyId}
          nationId={nationId}
          partyIdeology={partyIdeology}
          currentTick={currentTick}
          onRecruited={onRecruited}
          onAssignRole={onAssignRole}
        />
      )}
    </>
  )
}
